import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { apiUrl } from '../../../utils/apiCaller';

/**
 * Red undismissable banner that pins to the top of every page whenever a
 * tab has an active `imp_access_token`. Countdown is driven locally but
 * reconciled against `/api/impersonation/session/status` every 30s so
 * server-side revocation (auto TTL, force-end) kicks the UI out even if
 * the tab was idle.
 *
 * Mounted at App root, so it survives route changes. Pushes `<body>` down
 * by injecting a `padding-top` on <body> while mounted.
 */
interface SessionMeta {
    session_id: number;
    started_at: string;
    expires_at: string;
    target_user_email: string | null;
    target_user_fullname: string;
    target_company_id: number;
    target_company_name: string;
}

function readMeta(): SessionMeta | null {
    try {
        const raw = sessionStorage.getItem('imp_session_meta');
        if (!raw) return null;
        return JSON.parse(raw) as SessionMeta;
    } catch {
        return null;
    }
}

function clearImpersonation() {
    try {
        sessionStorage.removeItem('imp_access_token');
        sessionStorage.removeItem('imp_session_meta');
    } catch {
        /* ignore */
    }
}

function fmtMMSS(secondsRemaining: number): string {
    const s = Math.max(0, Math.floor(secondsRemaining));
    const mm = Math.floor(s / 60).toString().padStart(2, '0');
    const ss = (s % 60).toString().padStart(2, '0');
    return `${mm}:${ss}`;
}

const BANNER_HEIGHT = 44;

const ImpersonationBanner: React.FC = () => {
    const [meta, setMeta] = useState<SessionMeta | null>(() => readMeta());
    const [ending, setEnding] = useState(false);
    const [now, setNow] = useState(() => Date.now());
    const pollRef = useRef<number | null>(null);

    const hasSession = meta !== null && sessionStorage.getItem('imp_access_token') !== null;

    // 1s ticker for the countdown display.
    useEffect(() => {
        if (!hasSession) return;
        const id = window.setInterval(() => setNow(Date.now()), 1000);
        return () => window.clearInterval(id);
    }, [hasSession]);

    // Body offset: push tenant chrome down so it doesn't sit under the banner.
    useEffect(() => {
        if (!hasSession) return;
        const prev = document.body.style.paddingTop;
        document.body.style.paddingTop = `${BANNER_HEIGHT}px`;
        return () => {
            document.body.style.paddingTop = prev;
        };
    }, [hasSession]);

    const endSession = useCallback(async (localOnly = false) => {
        if (ending) return;
        setEnding(true);
        const token = sessionStorage.getItem('imp_access_token');
        if (!localOnly && token) {
            try {
                await axios.post(
                    apiUrl + 'impersonation/session/end',
                    {},
                    { headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' } },
                );
            } catch {
                // Proceed with local cleanup even if the server call fails —
                // leaving a bad token behind is worse than a stale session row.
            }
        }
        clearImpersonation();
        setMeta(null);
        // Try to close the tab. window.close() returns undefined regardless
        // of success; if it works, the tab is gone before the next line
        // runs. If the browser blocks it (tab wasn't script-opened, or
        // user has a strict policy), replace() kicks in and the user lands
        // on /login instead of staring at a dead tenant session.
        window.close();
        window.location.replace('/login');
    }, [ending]);

    // Server reconciliation every 30s. If the server says expired/ended, we end locally.
    useEffect(() => {
        if (!hasSession) return;

        const tick = async () => {
            const token = sessionStorage.getItem('imp_access_token');
            if (!token) {
                setMeta(null);
                return;
            }
            try {
                const res = await axios.get<{ expired: boolean; ended_at: string | null; seconds_remaining: number }>(
                    apiUrl + 'impersonation/session/status',
                    { headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' } },
                );
                if (res.data.ended_at !== null || res.data.expired) {
                    endSession(true);
                }
            } catch (err: any) {
                // 401 means the server already ended/revoked it. Clean up.
                if (err?.response?.status === 401) {
                    endSession(true);
                }
            }
        };

        pollRef.current = window.setInterval(tick, 30000);
        return () => {
            if (pollRef.current !== null) window.clearInterval(pollRef.current);
        };
    }, [hasSession, endSession]);

    // Local timer also auto-ends when countdown hits 0 — don't wait for the
    // 30s poll to catch up.
    useEffect(() => {
        if (!hasSession || !meta) return;
        const expiresMs = Date.parse(meta.expires_at);
        if (isFinite(expiresMs) && now >= expiresMs) {
            endSession(true);
        }
    }, [hasSession, meta, now, endSession]);

    if (!hasSession || !meta) return null;

    const expiresMs = Date.parse(meta.expires_at);
    const secondsRemaining = isFinite(expiresMs) ? (expiresMs - now) / 1000 : 0;

    return (
        <div
            role="alert"
            aria-live="assertive"
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                height: BANNER_HEIGHT,
                background: '#b91c1c',
                color: '#fff',
                zIndex: 2147483647,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '0 16px',
                fontFamily: 'Inter, system-ui, sans-serif',
                fontSize: 13,
                fontWeight: 500,
                boxShadow: '0 2px 12px rgba(185, 28, 28, 0.35)',
                letterSpacing: 0.1,
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                <span
                    style={{
                        display: 'inline-block',
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: '#fff',
                        boxShadow: '0 0 0 3px rgba(255,255,255,0.2)',
                        animation: 'imp-pulse 1.5s ease-in-out infinite',
                        flex: '0 0 auto',
                    }}
                />
                <span
                    style={{
                        textTransform: 'uppercase',
                        fontSize: 11,
                        letterSpacing: '0.1em',
                        opacity: 0.85,
                    }}
                >
                    Impersonating
                </span>
                <span
                    style={{
                        fontWeight: 600,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {meta.target_user_email || meta.target_user_fullname}
                </span>
                <span style={{ opacity: 0.6 }}>·</span>
                <span
                    style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {meta.target_company_name}
                </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, flex: '0 0 auto' }}>
                <span
                    style={{
                        fontFamily: 'JetBrains Mono, ui-monospace, Consolas, monospace',
                        fontSize: 12,
                        opacity: 0.9,
                        fontVariantNumeric: 'tabular-nums',
                    }}
                    title="Time remaining"
                >
                    {fmtMMSS(secondsRemaining)}
                </span>
                <button
                    type="button"
                    onClick={() => endSession(false)}
                    disabled={ending}
                    style={{
                        background: '#fff',
                        color: '#b91c1c',
                        border: 'none',
                        borderRadius: 4,
                        padding: '5px 12px',
                        fontSize: 12,
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.06em',
                        cursor: ending ? 'wait' : 'pointer',
                        opacity: ending ? 0.7 : 1,
                    }}
                >
                    {ending ? 'Ending…' : 'End session'}
                </button>
            </div>
            <style>{`@keyframes imp-pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.3; } }`}</style>
        </div>
    );
};

export default ImpersonationBanner;
